/* @flow */
import { isNil } from "shared/src/utils/util";
import {
    FacetFilterBool,
    FacetFilterMulti,
    FacetFilterNumeric,
    FacetResult
} from "shared/src/types/articlesearch/Facets";
import Tracking from "shared/src/uidispatcherapi/Tracking";

// 'private' helper method
function arrayToMap(array: string[]): {
    [x: string]: boolean;
} {
    return (array ?? []).reduce((accumulator, currentElement) => {
        accumulator[currentElement] = true;
        return accumulator;
    }, {} as { [x: string]: boolean });
}

type MultiMapperTypeString = (x0: FacetFilterMulti) => string;
type NumericMapperType = (x0: FacetFilterNumeric) => string;
const multiFacetToString: MultiMapperTypeString = (facet) => {
    const decodedValues = (facet.values ?? []).map((x) => encodeURI(x));
    return facet.key + ":" + decodedValues.join(",");
};
const numericFacetToString: NumericMapperType = (facet) =>
    facet.key + ":[" + (facet.range ? facet.range.join("~") : "") + "]";
const boolFacetToString = (facet: { key: string; value: boolean }) =>
    facet.key + ":" + (facet.value ? "true" : "false");

export function getFacetMap(arrayOfFacets: Array<FacetFilterMulti>): {
    [x: string]: {
        [x: string]: boolean;
    };
} {
    if (!arrayOfFacets) {
        return {};
    }
    return (arrayOfFacets ?? []).reduce((accumulator, currentObject) => {
        accumulator[currentObject.key] = arrayToMap(currentObject.values);
        return accumulator;
    }, {} as { [x: string]: { [y: string]: boolean } });
}

export function getBoolFacetMap(boolFacetFilters: Array<FacetFilterBool>): {
    [x: string]: boolean | undefined;
} {
    return (boolFacetFilters ?? []).reduce((accumulator, currentObject) => {
        accumulator[currentObject.key] = currentObject.value;
        return accumulator;
    }, {} as { [x: string]: boolean | undefined });
}

export function trueAmount(facet: FacetResult): number {
    return facet.values?.filter((value) => value.name === "true")[0]?.amount ?? 0;
}

// General parser for all facet types. Custom part is encoded in the mapper
function parseFacetString<T>(
    facetString: string | undefined | null,
    mapper: (key: string, value: string) => T
): Array<T> {
    if (!facetString) {
        return [];
    }
    const decodedFacetString = decodeURI(facetString);
    const individualFacetStrings = decodedFacetString.split(";");
    const facets = (individualFacetStrings ?? []).map((facetString) => {
        const facetArray = facetString.split(":");
        if (facetArray.length !== 2 /* key/name and (list of) values */) {
            Tracking.trackError("searchdiscover", "ui", "FaceUtil", "Error when parsing facet " + facetString);
            return null;
        }
        return mapper(facetArray[0], facetArray[1]);
    });
    return facets.filter((facet) => facet !== null && facet !== undefined) as Array<T>;
}

export function parseMultiFacetString(facetString: string | undefined | null): Array<FacetFilterMulti> {
    // Example for facet-url-parameter: brand:horeca select,aro;color:yellow,white (uri encoded)
    const multiMapper = (key: string, value: string) => {
        return { key, values: value.split(",") };
    };
    return parseFacetString(facetString, multiMapper);
}

export function buildMultiFacetQueryString(facets: FacetFilterMulti[] | undefined | null): string | undefined {
    if (!facets || facets.length === 0) {
        return undefined;
    }
    return (facets ?? []).reduce((accumulatorString, facet) => {
        if (!facet.key || !facet.values || !facet.values.length) {
            return accumulatorString;
        }
        accumulatorString += accumulatorString === "" ? "" : ";";
        return accumulatorString + multiFacetToString(facet);
    }, "");
}

export function selectMultiFacet(
    currentFacets: Array<FacetFilterMulti>,
    key: string,
    value: string,
    selected: boolean
): Array<FacetFilterMulti> {
    const currentSelected = (currentFacets ?? []).find((f) => f.key === key) || {
        key: key,
        values: []
    };

    const updatedFacet = createMultiFacet(currentSelected, value, selected);
    const withModifiedFacet = (function () {
        const combined = [updatedFacet, ...(currentFacets ?? [])];
        const seen = new Set();

        return combined.filter((facet) => {
            const key = facet.key;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    })();
    return (withModifiedFacet ?? []).filter((item) => item.values.length > 0);
}

function createMultiFacet(currentSelected: FacetFilterMulti, value: string, selected: boolean) {
    const valuesWithOutSelected = currentSelected.values.filter((v) => v !== value);
    if (selected) {
        valuesWithOutSelected.push(value);
    }

    return { key: currentSelected.key, values: valuesWithOutSelected };
}

export function deselectAttribute(currentFacets: FacetFilterMulti[], key: string): FacetFilterMulti[] {
    return (currentFacets ?? []).filter((f) => f.key !== key);
}

// -------------------------- NUMERIC FACET UTIL FUNCTIONS ---------------------------

export function getNumericFacetMap(arrayOfFacets: Array<FacetFilterNumeric>): {
    [x: string]: {
        selectedMin: number | undefined | null;
        selectedMax: number | undefined | null;
    };
} {
    if (!arrayOfFacets) {
        return {};
    }

    // Format for numeric facets: [15,65] or [15, null]
    return (arrayOfFacets ?? []).reduce(
        (accumulator, { key, range }) => {
            if (range && range.length === 2) {
                accumulator[key] = {
                    selectedMin: range[0],
                    selectedMax: range[1]
                };
            }
            return accumulator;
        },
        {} as {
            [x: string]: {
                selectedMin: number | undefined | null;
                selectedMax: number | undefined | null;
            };
        }
    );
}

export function changeNumericFacet(
    currentFacets: Array<FacetFilterNumeric>,
    key: string,
    minValue: number | undefined | null,
    maxValue: number | undefined | null
): Array<FacetFilterNumeric> {
    const newFacet = createNumericFacet(key, minValue, maxValue);
    const seen = { [newFacet.key]: true };
    const withModifiedFacet = [
        newFacet,
        ...currentFacets.filter((facet) => {
            if (seen[facet.key]) return false;
            seen[facet.key] = true;
            return true;
        })
    ];
    return (withModifiedFacet ?? []).filter((item) => !!item.range);
}

function createNumericFacet(
    key: string,
    minValue: number | undefined | null,
    maxValue: number | undefined | null
): FacetFilterNumeric {
    if (!isNil(minValue) || !isNil(maxValue)) {
        return { key: key, range: [minValue, maxValue] };
    } else {
        return { key: key, range: null };
    }
}

export function parseNumericFacetString(facetString: string | undefined | null): Array<FacetFilterNumeric> {
    // Example for facet-url-parameter: suggestedNumberOfPortions:[15~], semicolon separated (uri encoded)
    const numericMapper = (key: string, value: string): FacetFilterNumeric => {
        const withoutBrackets = value.replace(/[\[\]]/g, "");
        const numberStrings = withoutBrackets.split("~");
        if (numberStrings.length !== 2) {
            return { key, range: null };
        }
        const [min, max] = numberStrings.map((numberString) => (numberString ? Number(numberString) : null));
        return { key, range: [min, max] };
    };
    return parseFacetString(facetString, numericMapper);
}

export function buildNumericFacetQueryString(facets: FacetFilterNumeric[] | undefined | null): string | undefined {
    if (!facets || facets.length === 0) {
        return undefined;
    }
    return facets.reduce((accumulatorString, facet) => {
        if (!facet.key || !facet.range) {
            return accumulatorString;
        }
        accumulatorString += accumulatorString === "" ? "" : ";";
        return accumulatorString + numericFacetToString(facet);
    }, "");
}

export function minWithoutNull(a: number | undefined | null, b: number | undefined | null) {
    if (a === null || a === undefined) {
        return b;
    }
    if (b === null || b === undefined) {
        return a;
    }
    return a < b ? a : b;
}

export function maxWithoutNull(a: number | undefined | null, b: number | undefined | null) {
    if (a === null || a === undefined) {
        return b;
    }
    if (b === null || b === undefined) {
        return a;
    }
    return a > b ? a : b;
}

// We need to use integers for small number scaling as well, because otherwise
// we get rounding artifacts in the display.
export function computeRangeForFacet(facet: FacetResult): {
    scale: number;
    small: boolean;
} {
    let diff = Math.abs(Number(facet.numberMax) - Number(facet.numberMin));
    if (isNaN(diff) || diff === 0) {
        return { scale: 1, small: false };
    }
    let scale = 1;
    if (diff < 10) {
        while (diff < 10) {
            scale *= 10;
            diff *= 10;
        }
        return { scale: scale, small: true };
    }
    if (diff > 100) {
        while (diff > 100) {
            scale *= 10;
            diff /= 10;
        }
        return { scale: scale, small: false };
    }
    return { scale: 1, small: false };
}

export function roundNumericFacetValue(
    value: number,
    scaleObject: {
        scale: number;
        small: boolean;
    },
    updown?: boolean | undefined | null
): number {
    const scale = scaleObject.scale;
    let round = Math.round;
    if (updown === true) {
        round = Math.ceil;
    } else if (updown === false) {
        round = Math.floor;
    }
    return scaleObject.small ? round(value * scale) / scale : round(value / scale) * scale;
}

export function getNumericFacetRange(facet: FacetResult): {
    maxValue: number;
    minValue: number;
} {
    const scaleObject = computeRangeForFacet(facet);
    const maxValue = roundNumericFacetValue(Number(facet.numberMax), scaleObject, true);
    const minValue = roundNumericFacetValue(Number(facet.numberMin), scaleObject, false);
    return { maxValue, minValue };
}

export function numericFacetIsTrivial(facet: FacetResult): boolean {
    const { maxValue, minValue } = getNumericFacetRange(facet);
    // The original range might be trivial, while the rounded range isn't. This is because we round the left side down
    // and the right side up, so if the original range is [0.75, 0.75], the rounded range is [0, 1].
    return maxValue === minValue || facet.numberMax === facet.numberMin;
}

// =============================== END NUMERIC FACET UTIL FUNCTIONS ===========================

export function changeBoolFacet(
    currentFacets: Array<FacetFilterBool>,
    key: string,
    value: boolean | null
): Array<FacetFilterBool> {
    if (value === null) {
        return (currentFacets ?? []).filter((facet) => facet.key !== key);
    } else {
        const newFacet = { key, value };
        const seen = { [key]: true };
        return [
            newFacet,
            ...(currentFacets ?? []).filter((facet) => {
                if (seen[facet.key]) return false;
                seen[facet.key] = true;
                return true;
            })
        ];
    }
}

export function parseBoolFacetString(facetString: string): Array<FacetFilterBool> {
    return parseFacetString(facetString, (key, value) => ({
        key,
        value: value === "true"
    }));
}

export function buildBoolFacetQueryString(options: Array<FacetFilterBool>): string | undefined {
    if (!options || options.length === 0) {
        return undefined;
    }
    return options.map(boolFacetToString).join(";");
}
